import React, {
  createContext,
  CSSProperties,
  Dispatch,
  MouseEventHandler,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import clsx from 'clsx'
import { useAtom } from 'jotai'

import { AppBreakpoint } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { ChevronDown, ChevronUp } from '@makyoapp/makyo-icon/icon'

import { editSettingsAtom } from '@/lib/jotai'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useActiveDevice } from '@/hooks/useActiveDevice'
import { BuilderComponentSetting, BuilderComponentSettingType } from '@/shared/builder'
import {
  formatResponsiveClampedValue,
  formatResponsiveSettingValues,
  hasResponsiveSettings,
} from '@/shared/builder/viewport/responsive-settings'

import { BrandKitFontV2 } from '../BrandKit/BrandKitFontV2'

const AccordionContext = createContext<{
  activeItems: string[]
  setActiveItems: Dispatch<SetStateAction<string[]>>
  onSelect?: (itemId: string, nextActive: boolean) => void
  openMultiple: boolean
}>({
  activeItems: [],
  setActiveItems: () => null,
  openMultiple: true,
})

interface AccordionProps {
  initialActiveItems?: string[]
  children?: React.ReactNode
  onSelect?: (itemId: string, nextActive: boolean) => void
  openMultiple?: boolean
}

interface EditSettingProps {
  settingKey: string
  updatedValue: any
}

export function Accordion({ initialActiveItems = [], onSelect, children, openMultiple = true }: AccordionProps) {
  const [activeItems, setActiveItems] = useState(initialActiveItems)

  return (
    <AccordionContext.Provider
      value={{
        activeItems,
        setActiveItems,
        onSelect,
        openMultiple,
      }}
    >
      <div className='mk-accordion'>{children}</div>
    </AccordionContext.Provider>
  )
}

interface AccordionItemProps {
  itemId: string
  title: React.ReactNode
  children?: React.ReactNode
  className?: string
  style?: CSSProperties
  headerClassName?: string
  headerTextClassName?: string
  contentClassName?: string
  defaultHeight?: boolean
  defaultStyle?: boolean
  tag?: keyof JSX.IntrinsicElements
  onMouseEnter?: MouseEventHandler<HTMLDivElement>
  onMouseLeave?: MouseEventHandler<HTMLDivElement>
  onClick?: (e: React.MouseEvent) => void
  righSection?: React.ReactNode
  isLoading?: boolean
  items?: BuilderComponentSetting[]
}

type NestedTypographySettings = Record<AppBreakpoint, string>

type ParentTypographySettings = Record<string, NestedTypographySettings>

function hasResponsiveSettingsRecursive(item: BuilderComponentSetting): boolean {
  if (!item) return false

  if (item.id && hasResponsiveSettings(item.id)) {
    return true
  }

  if (item.type === BuilderComponentSettingType.Group && item.groupItems) {
    return Object.values(item.groupItems).some((groupItems) =>
      groupItems.some((subItem) => hasResponsiveSettingsRecursive(subItem))
    )
  }

  return false
}

function collectResponsiveSettingsFromGroup(
  item: BuilderComponentSetting,
  initialCompSettings: Record<string, any>,
  device: AppBreakpoint,
  shouldValueBeClamped: boolean
): Record<string, any> {
  const result: Record<string, any> = {}

  if (hasResponsiveSettings(item.id)) {
    const currentSettings = initialCompSettings[item.id]
    const isTypographyGroup = Object.keys(currentSettings || {}).some((key) => key.includes('typography'))

    let currentDeviceValue
    if (isTypographyGroup) {
      currentDeviceValue = Object.entries(currentSettings || {}).reduce((acc, [key, value]) => {
        return {
          ...acc,
          [key]: (value as any)[device],
        }
      }, {})
    } else {
      currentDeviceValue = currentSettings?.[device]
    }

    result[item.id] = shouldValueBeClamped
      ? formatResponsiveClampedValue(currentSettings, currentDeviceValue)
      : formatResponsiveSettingValues(currentSettings, currentDeviceValue)
  }

  if (item.type === BuilderComponentSettingType.Group && item.groupItems) {
    Object.values(item.groupItems).forEach((groupItems) => {
      groupItems.forEach((subItem) => {
        const subResult = collectResponsiveSettingsFromGroup(subItem, initialCompSettings, device, shouldValueBeClamped)
        Object.assign(result, subResult)
      })
    })
  }

  return result
}

export function AccordionItem({
  itemId,
  title,
  className,
  style,
  children,
  headerClassName,
  headerTextClassName,
  contentClassName,
  defaultHeight = true,
  defaultStyle = true,
  onMouseLeave,
  onMouseEnter,
  onClick,
  tag: Tag = 'button',
  righSection,
  isLoading,
  items,
}: AccordionItemProps) {
  const { t } = useTranslation()

  const [device] = useActiveDevice()
  const { activeItems, setActiveItems, onSelect, openMultiple } = useContext(AccordionContext)

  const isActive = useMemo(() => {
    return activeItems.indexOf(itemId) !== -1
  }, [activeItems, itemId])

  const [activeEditSetting, setActiveEditSetting] = useAtom(editSettingsAtom)
  const isShowApplyButton = useMemo(() => {
    return items?.find((item) => item?.id === activeEditSetting.settingKey && device === activeEditSetting.device)
  }, [items, activeEditSetting.settingKey, activeEditSetting.device, device])

  const { activeComponent, activeLayerClient } = useActiveLayerContext()
  const activeComponentId = activeComponent?.id
  const [isApplying, setIsApplying] = useState(false)

  const isTypographyGroup = useMemo(() => {
    return items?.some((item) => item?.id?.includes('typography'))
  }, [items])

  const handleApplyChanges = async () => {
    if (!activeComponentId) return

    setIsApplying(true)
    try {
      let component = activeLayerClient?.componentsMap?.get(activeComponentId)
      let initialCompSettings = component?.settings ?? {}
      const shouldValueBeClamped = items?.some((item) => item?.clampInAllDevices)
      // Get current settings for the active items (including Group items recursively)
      const settingsToApply = items?.reduce((acc, item) => {
        const groupResult = collectResponsiveSettingsFromGroup(
          item,
          initialCompSettings,
          device,
          shouldValueBeClamped ?? false
        )
        return {
          ...acc,
          ...groupResult,
        }
      }, {})

      if (settingsToApply && Object.keys(settingsToApply).length > 0) {
        const updatedCompSettings = {
          ...initialCompSettings,
          ...settingsToApply,
        }

        component?.UpdateAllSettings(updatedCompSettings)
        toast.success(t('Changes applied successfully'))
        setActiveEditSetting({ device, settingKey: '', updatedValues: [] })
      }
    } finally {
      setTimeout(() => {
        setIsApplying(false)
      }, 3000)
    }
  }

  const isResponsive = useMemo(() => {
    if (!items || items.length === 0) return false
    return items.some((setting) => {
      if (!setting) return false
      return hasResponsiveSettingsRecursive(setting)
    })
  }, [items])
  return (
    <div
      className={clsx('mk-accordion-item', defaultStyle && 'mk-accordion-style', className)}
      style={style}
      data-item-id={itemId}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={(e) => (onClick ? onClick(e) : null)}
    >
      <Tag
        className={clsx('mk-accordion-header', defaultHeight && 'h-12', headerClassName)}
        onClick={() => {
          onSelect?.(itemId, !isActive)
          setActiveItems((prev) => {
            return openMultiple
              ? isActive
                ? prev.filter((p) => p !== itemId)
                : [...prev, itemId]
              : isActive
                ? []
                : [itemId]
          })
        }}
      >
        <div className={clsx('mk-accordion-header-text', headerTextClassName)}>{title}</div>
        {righSection && <div className='mr-2'>{righSection}</div>}
        {isLoading ? <span className='loading spin' /> : isActive ? <ChevronUp /> : <ChevronDown />}
      </Tag>

      {isActive && <div className={clsx('mk-accordion-content', contentClassName)}>{children}</div>}

      {isResponsive && isActive && (
        <div className='w-[calc(100% - 24px)] mb-6 mx-4 flex flex-col items-center justify-center gap-3'>
          <button
            className={clsx(
              'text-gray-100 text-xs py-2 px-3 rounded-sm w-full transition-colors',
              isApplying
                ? 'bg-primary-700 dark:bg-primary-700/85 cursor-wait'
                : 'bg-primary-500 dark:bg-primary-500/85 hover:bg-primary-600'
            )}
            onClick={handleApplyChanges}
            disabled={isApplying}
          >
            {isApplying ? t('Saving changes...') : t('Save changes across all screen sizes')}
          </button>
          {isTypographyGroup && <BrandKitFontV2 fromSettingField />}
        </div>
      )}
    </div>
  )
}
